import { ApiConfiguration } from '../config/ApiConfiguration.js';
import { HttpTransport } from '../transport/HttpTransport.js';
import { AuthManager } from '../auth/AuthManager.js';
import { RetryPolicy } from '../retry/RetryPolicy.js';
import { attachFlushOnHide } from '../queue/PageLifecycle.js';
import { EventQueue } from '../queue/EventQueue.js';
import { ApiLogger } from '../logging/ApiLogger.js';
import { ApiAuthError, ApiResponseError } from '../errors/ApiError.js';
import { mapAdBreaks } from '../models/AdBreakSchedule.js';
import { toAuthStatus } from '../models/AuthStatus.js';
import { randomId } from '../ids.js';
import { SDK_VERSION } from '../../core/version.js';
import {
  buildAdBreakResolveRequest,
  buildErrorReport,
  buildEventBatch,
  buildHeartbeat,
  buildTrackingRelayRequest,
} from '../dto/requests.js';

// The single entry point to the Playbid backend.
//
// Speaks the surface the platform actually serves (docs/telemetry/03, as
// implemented by the Playbid API; the SDK-facing calls are listed in the
// platform's API reference):
//
//   GET  /api/v1/config         bootstrap: exchanges the site key for a bearer
//   POST /api/v1/events         batched catalog events (impressions, quartiles,
//                               clicks, errors) - what the dashboard counts
//   POST /api/v1/errors         one failure, with its code and message
//   POST /api/v1/heartbeat      page-level liveness ("is the SDK alive here")
//   POST /v1/tracking/relay     our record of each VAST tracker we fired
//   POST /v1/adbreaks/resolve   the site's configured audio break schedule
//   GET  /v1/sdk-config/{key}   the site's unit configuration
//
// Two response disciplines, deliberately different:
//
//   AWAITED (config, ad breaks) reject with a classified ApiError the caller is
//   expected to handle - these drive what plays, so a failure must be visible.
//
//   FIRE-AND-FORGET (events, errors, heartbeat, relay) NEVER reject. They
//   resolve to a boolean. A telemetry failure must never reach playback or
//   publisher code.
export class PlayBidApiClient {
  #config;
  #transport;
  #auth;
  #retry;
  #logger;
  #eventQueue;
  #detachLifecycle = () => {};
  #destroyed = false;
  #startedAt = Date.now();

  constructor(options = {}) {
    const { config, services = {} } = options;

    this.#config = config instanceof ApiConfiguration ? config : new ApiConfiguration(config);
    this.#logger = services.logger ?? new ApiLogger(this.#config.logging);
    this.#transport =
      services.transport ??
      new HttpTransport({
        config: this.#config,
        services: { logger: this.#logger, fetch: services.fetch ?? null },
      });
    this.#auth =
      services.auth ??
      new AuthManager({ config: this.#config, services: { transport: this.#transport } });
    this.#retry = services.retryPolicy ?? new RetryPolicy();
    // A `/api/v1/config` response the caller already holds (boot()'s B2).
    if (options.initialAuth) this.#auth.seed?.(options.initialAuth);

    this.#eventQueue = new EventQueue({
      // The backend's hard ceiling is 50 events per batch.
      batchSize: Math.min(this.#config.telemetry.batchSize, MAX_EVENTS_PER_BATCH),
      maxQueueSize: this.#config.telemetry.maxQueueSize,
      flushIntervalMs: this.#config.telemetry.flushIntervalMs,
      onFlush: (batch) => this.#sendEventBatch(batch),
    });

    // Without this, a visitor who closes the tab mid-interval takes up to
    // flushIntervalMs of events with them - and on a bounce that is most of the
    // session. The batch send already sets keepalive, so the request survives
    // the document; what was missing was anything to trigger it.
    this.#detachLifecycle = attachFlushOnHide(() => this.#eventQueue.flush());
  }

  get authStatus() {
    return toAuthStatus(this.#auth.status);
  }

  // Whether advertisement-category events are reported (see
  // DEFAULT_API.telemetry.adEvents for why boot() turns them off).
  get reportsAdEvents() {
    return this.#config.telemetry.adEvents !== false;
  }

  get queuedEventCount() {
    return this.#eventQueue.size;
  }

  // The site key the backend resolved this page to. Equal to the configured
  // key; exposed so a status panel can show which site is reporting.
  get publisherId() {
    return this.#config.publisherId;
  }

  // ---- GET /api/v1/config ------------------------------------------------
  async authenticate() {
    await this.#auth.authenticate();
    return this.authStatus;
  }

  // ---- GET /v1/sdk-config/{siteKey} -------------------------------------
  // The site's unit configuration, exactly as the backend serves it.
  async fetchSdkConfig() {
    const path = `/v1/sdk-config/${encodeURIComponent(this.#config.apiKey)}`;
    const response = await this.#send({ method: 'GET', path, retryKey: 'sdk-config', auth: false });
    if (!response.data || typeof response.data !== 'object') {
      throw new ApiResponseError('sdk-config response was not a JSON object', {
        endpoint: '/v1/sdk-config',
        context: { endpoint: '/v1/sdk-config' },
      });
    }
    return response.data;
  }

  // ---- POST /v1/adbreaks/resolve -----------------------------------------
  async resolveAdBreaks(options = {}) {
    const { contentId, contentDurationSec, adSlots, midRollPositionsSec } = options;
    const response = await this.#send({
      method: 'POST',
      path: '/v1/adbreaks/resolve',
      auth: false,
      body: buildAdBreakResolveRequest({
        apiKey: this.#config.apiKey,
        contentId,
        contentDurationSec,
        adSlots,
        midRollPositionsSec,
      }),
    });
    return mapAdBreaks(response.data);
  }

  // ---- POST /api/v1/events (fire-and-forget, batched) --------------------
  //
  // Takes one catalog event (see integration/telemetryCatalog.js) and queues
  // it. The queue flushes on size, on its interval, and when the page hides.
  publishEvent(event) {
    if (this.#destroyed || !this.#config.telemetry.enabled || !event) return;
    this.#eventQueue.enqueue(event);
  }

  async flushEvents() {
    await this.#eventQueue.flush();
  }

  // ---- POST /v1/tracking/relay (fire-and-forget) -------------------------
  async relayTracking(options = {}) {
    const { event, url, requestId, outcome, reason } = options;
    if (!url) return false;
    return this.#sendTelemetry('/v1/tracking/relay', {
      auth: false,
      body: buildTrackingRelayRequest({
        apiKey: this.#config.apiKey,
        requestId,
        event,
        url,
        outcome,
        reason,
      }),
    });
  }

  // ---- POST /api/v1/errors (fire-and-forget) -----------------------------
  async reportError(options = {}) {
    const { error, context, timestamp } = options;
    if (!error || !context?.sessionId || !context?.widgetId || !context?.widgetType) return false;
    const errorId = randomId();
    return this.#sendTelemetry('/api/v1/errors', {
      idempotencyKey: errorId,
      body: buildErrorReport({
        errorId,
        publisherId: this.#config.publisherId,
        error,
        context,
        timestamp,
      }),
    });
  }

  // ---- POST /api/v1/heartbeat (fire-and-forget) --------------------------
  async sendHeartbeat(widgets = []) {
    const beaconId = randomId();
    return this.#sendTelemetry('/api/v1/heartbeat', {
      idempotencyKey: beaconId,
      body: buildHeartbeat({
        beaconId,
        publisherId: this.#config.publisherId,
        sentAt: Date.now(),
        sdkVersion: SDK_VERSION,
        uptimeMs: Date.now() - this.#startedAt,
        widgets,
      }),
    });
  }

  async destroy() {
    if (this.#destroyed) return;
    this.#destroyed = true;
    // Detach BEFORE stopping the queue: a hide event arriving mid-teardown
    // would otherwise flush a queue that is already draining.
    this.#detachLifecycle();
    await this.#eventQueue.stop();
    this.#auth.clear();
  }

  // ---- internals ---------------------------------------------------------

  // The one place a request is authenticated, retried and normalized.
  async #send({
    method,
    path,
    query,
    body,
    retryKey,
    keepalive = false,
    auth = true,
    headers = {},
  }) {
    const key = retryKey ?? path;
    const maxAttempts = this.#retry.maxAttempts(key);
    let attempt = 0;
    let lastError;

    while (attempt < maxAttempts) {
      attempt += 1;
      try {
        const authHeaders = auth ? await this.#authHeaders() : {};
        return await this.#transport.request({
          method,
          path,
          query,
          body,
          headers: { ...headers, ...authHeaders },
          keepalive,
        });
      } catch (error) {
        lastError = error;

        // A 401 means the bearer we held is no longer valid. Clearing it makes
        // the next attempt re-authenticate rather than replay the dead token.
        if (error instanceof ApiAuthError) {
          this.#auth.clear();
        }

        if (!this.#retry.shouldRetry(key, error, attempt)) break;
        await delay(this.#retry.delayFor(attempt));
      }
    }

    throw lastError;
  }

  async #authHeaders() {
    if (!this.#config.auth.enabled) return {};
    return this.#auth.authorizationHeader();
  }

  // Fire-and-forget wrapper. Resolves true/false and NEVER rejects - the
  // isolation boundary that keeps a telemetry failure away from playback.
  async #sendTelemetry(path, { body, auth = true, idempotencyKey = null }) {
    if (this.#destroyed || !this.#config.telemetry.enabled) return false;
    try {
      await this.#send({
        method: 'POST',
        path,
        body,
        auth,
        keepalive: true,
        // The backend replays its original answer for a repeated key, so a
        // retry whose first response was lost can never double-count.
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
      });
      return true;
    } catch (error) {
      this.#logger.telemetryFailure(path, error);
      return false;
    }
  }

  async #sendEventBatch(events) {
    if (events.length === 0) return;
    // Built once, outside the retry loop: every attempt carries the same
    // batchId, which is what the backend deduplicates on.
    const batchId = randomId();
    const body = buildEventBatch({
      batchId,
      publisherId: this.#config.publisherId,
      sentAt: Date.now(),
      events,
    });
    await this.#send({
      method: 'POST',
      path: '/api/v1/events',
      body,
      keepalive: true,
      headers: { 'Idempotency-Key': batchId },
    });
  }
}

const MAX_EVENTS_PER_BATCH = 50;

function delay(ms) {
  return ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();
}
